<template>
    <ValidationObserver v-slot="{ invalid, handleSubmit, failed }" ref="observer">
        <form class="person-form" @submit.prevent="handleSubmit(savePerson)" novalidate>
            <div class="edit-box box-shadow person" :class="{ 'is-locked' : !isEditMode }">
                <div class="columns">
                    <div class="column">
                        <div class="edit-box__header is-clearfix">
                            <div class="edit-box__headline-wrapper is-pulled-left">
                            <span class="edit-box__headline-icon icon">
                                <i v-if="personCopy.person_type === 'pet'" class="fa fa-paw" aria-hidden="true"></i>
                                <i v-if="personCopy.person_type === 'adult'" class="fa fa-user" aria-hidden="true"></i>
                                <i v-if="personCopy.person_type === 'child'" class="fa fa-child" aria-hidden="true"></i>
                            </span>
                                <h3 v-if="!isEditMode" class="edit-box__headline is-inline-block">
                                    {{ personCopy.name }} </h3>
                                <h4 v-if="!isEditMode">
                                    <template v-if="personCopy.code">
                                        Code: <strong>{{ personCopy.code }}</strong> |
                                    </template>
                                    <template v-if="personCopy.external_id">
                                        {{ $t('persons.external_id') }}: <strong>{{ personCopy.external_id }}</strong> |
                                    </template>
                                    {{ `${personTypes[personCopy.person_type]}` }}
                                    <template v-if="personCopy.person_type !== 'pet'">
                                        {{
                                            `| ${$t('persons.age_from')} ${personCopy.age_from}-${personCopy.age_to}`
                                        }}
                                    </template>
                                    <template v-if="personCopy.can_be_free">
                                        | <i class="fa fa-euro fa-lg red" aria-hidden="true"></i> {{ $t('persons.can_be_free') }}
                                    </template>
                                </h4>
                                <ValidationProvider
                                    v-if="isEditMode"
                                    name="personCopy.name"
                                    v-slot="{ errors }"
                                    rules="required">
                                    <input
                                        type="text"
                                        placeholder="Name"
                                        class="edit-box--input input is-inline-block"
                                        :class="{'is-danger': errors[0] }" name="personCopy.name"
                                        v-model="personCopy.name" @click="fromFieldClicked('name')">
                                    <h4 v-if="personCopy.code" class="person-code-edit">Code:
                                        <strong>{{ personCopy.code }}</strong></h4>
                                    <span v-show="errors[0]" class="help is-danger">{{
                                            errors[0]
                                        }}</span>
                                </ValidationProvider>
                            </div>
                            <div class="edit-box__toggle-edit is-pulled-right" @click="toggleEditMode()">
                                <a class="button" v-show="!isEditMode">
                                    <i class="fa fa-pencil-square-o fa-lg"
                                       aria-hidden="true"></i>
                                </a>
                                <a class="button" v-show="isEditMode"><i class="fa fa-times fa-lg" aria-hidden="true"></i></a>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="edit-box__section person__wysiwyg" v-if="!isEditMode" v-html="personCopy.description"></div>
                <div class="edit-box__section person__wysiwyg" v-if="isEditMode">
                    <wysiwyg v-model="personCopy.description" v-if="isEditMode" :options="{maxChars: 300}" @click.native="fromFieldClicked('description')"></wysiwyg>
                </div>
                <div class="edit-box__section">
                    <div class="person__season">

                        <UseDayPrices :lang="lang" :only-day-prices="onlyDayPrices" :data-copy="personCopy"/>

                        <table class="table" v-if="!personCopy.id">
                            <thead>
                            <tr>
                                <th>&nbsp;</th>
                                <th class="person__season__table-head" v-for="(value, key) in campsiteTypes" v-if="campsiteTypes">
                                    <img class="campsitetype__icon" :src="'/static/img/icons/icon_' + key + '.png'"/>
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(season, index) in seasons" v-if="!season.is_lock && seasons">
                                <td>
                                    <span class="label edit-box__includes-inline-input">
                                        {{ season.name }}
                                        <span class="tooltip-activator d-inline-flex">
                                          <i class="fa fa-info icon-border" aria-hidden="true" />
                                          <span class="tooltip tooltip--is-large">{{ $t('bookings.newBookingInfo') }}</span>
                                        </span>
                                    </span>
                                </td>
                                <td v-for="(value, key) in campsiteTypes" v-if="campsiteTypes">
                                    <span v-if="!isEditMode"
                                          class="edit-box__includes-inline-input" :class="{ 'is-warning': !prices[season.id][key], 'is-warning': !prices[season.id][key] }">
                                        <span v-if="prices[season.id][key] != undefined">
                                            {{
                                                prices[season.id][key] | currency($getDefaultCurrency().code, lang)
                                            }}
                                        </span>
                                        <span v-if="prices[season.id][key] == undefined">
                                            <span class="icon is-small"><i aria-hidden="true" class="fa fa-exclamation-triangle is-invalid"></i></span>
                                        </span>
                                    </span>
                                    <ValidationProvider v-if="isEditMode" :name="index+key" v-slot="{ errors }" rules="required|min:1|max:999">
                                        <input
                                            class="input edit-box__inline-input is-price"
                                            type="number" id="price"
                                            :name="index+key" v-model="prices[season.id][key]"
                                            :class="{'is-danger': errors[0] || prices[season.id][key] == undefined }"
                                            @change="onPriceChanged(key, season.id, prices[season.id][key])"
                                            @click="fromFieldClicked('season_price')">
                                        <span class="edit-box__includes-inline-input">{{
                                                $getDefaultCurrency().symbol
                                            }}</span>
                                    </ValidationProvider>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div
                    v-if="isEditMode"
                    class="columns"
                >
                    <div class="column is-half">
                        <div class="edit-box__section">
                            <label for="person_type" class="label is-inline-block edit-box__includes-inline-input">
                                {{ $t('persons.type') }}
                            </label>
                            <ValidationProvider v-if="isEditMode" name="person_type" v-slot="{ errors }" rules="required">
                            <span class="select">
                                <select id="person_type" v-model="personCopy.person_type"
                                        :class="{'is-danger': errors[0] }"
                                        name="person_type" @click="fromFieldClicked('person_type')">
                                    <option v-for="(value, key) in personTypes" :value="key">
                                    {{ value }}
                                    </option>
                                </select>
                            </span>
                                <span v-show="errors[0]" class="help is-danger">{{
                                        errors[0]
                                    }}</span>
                            </ValidationProvider>

                            <!-- preparing for the next step to make flag -->
                            <div class="edit-box__section">
                                <br>
                                <label for="transit_item" class="checkbox label">
                                    {{ $t('persons.can_be_free') }}:
                                    <span class="icon is-small" v-if="!isEditMode">
                                        <i aria-hidden="true" class="fa fa-check-circle is-valid" v-if="personCopy.can_be_free"></i>
                                        <i aria-hidden="true" class="fa fa-minus-circle is-invalid" v-else></i>
                                    </span>

                                    <input v-if="isEditMode"
                                        class="edit-box__inline-input"
                                        type="checkbox"
                                        id="transit_item"
                                        name="transit_item"
                                        v-model="personCopy.can_be_free"
                                        @click="fromFieldClicked('transit_item')"
                                        >
                                </label>
                            <span>{{ $t('persons.can_be_free_info') }}</span>
                        </div>

                        </div>
                    </div>
                    <div
                        v-if="personCopy.person_type !== 'pet'"
                        class="column is-half"
                    >
                        <div>
                            <label
                                class="age-text edit-box__includes-inline-input"
                                for="age-from"
                            >
                                {{ $t('persons.age_from') }}
                            </label>
                            <input
                                class="input edit-box__inline-input is-price"
                                type="number"
                                id="age-from"
                                name="age-from"
                                v-model.number="personCopy.age_from"
                                @click="fromFieldClicked('age_from')">

                        </div>
                        <div>
                            <label
                                class="age-text edit-box__includes-inline-input"
                                for="age-to"
                            >
                                {{ $t('persons.age_to') }}
                            </label>
                            <input
                                class="input edit-box__inline-input is-price"
                                type="number"
                                id="age-to"
                                name="age-to"
                                v-model.number="personCopy.age_to"
                                @click="fromFieldClicked('age_to')">

                        </div>
                    </div>
                </div>

                <div class="edit-box__section" v-if="isEditMode">
                    <label for="external_id" class="label">
                        {{ $t('persons.external_id') }}:
                    </label>

                    <input v-if="isEditMode"
                           class="edit-box--input input is-inline-block"
                           type="text"
                           id="external_id"
                           name="personCopy.external_id"
                           v-model="personCopy.external_id"
                           @click="fromFieldClicked('transit_item')"
                    >

                    <span>{{ $t('persons.external_id_info') }}</span>
                </div>

                <!-- backend errors -->
                <div class="error-messages" v-if="isEditMode">
                <span class="help is-danger" v-for="(value, key) in apiErrors">
                    <span v-for="msg in value" v-html="msg"></span>
                </span>
                </div>

                <div class="edit-box__footer is-clearfix" v-if="isEditMode">
                    <a class="button is-link is-pulled-left" v-if="personCopy.id" @click="deletePerson()">
                    <span class="icon">
                        <i class="fa fa-trash" aria-hidden="true"></i>
                    </span>
                        <span>{{ $t('generic.delete') }}</span>
                    </a>
                    <button class="button is-primary is-pulled-right"
                            :class="{ 'is-loading': isLoading, 'is-disabled': failed }">
                        {{ $t('generic.save') }}
                    </button>
                    <a class="button is-link is-pulled-right" @click="toggleEditMode()">{{
                            $t('generic.cancel')
                        }}</a>
                </div>
                <div class="edit-box__dragger" v-if="!isEditMode">
                    <div class="handle">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </div>
            </div>
        </form>
    </ValidationObserver>
</template>

<script>
import {cloneDeep, find} from 'lodash';

import filters from '../../../filters';
import {Api} from '../../../api';
import {AppModel} from '../../../models';
import {EventBus, Tracking} from '../../../utils';
import {Wysiwyg} from '../../components';
import UseDayPrices from '../tools/UseDayPrices.vue';

export default {
    name: 'person',

    props: {
        value: {
            type: Object,
            'default': () => ([]),
        },

        seasons: {
            type: Array,
            'default': () => ([]),
        }
    },

    filters: filters,

    components: {
        UseDayPrices,
        Wysiwyg,
    },

    created() {
        // copy original data to object
        this.personCopy = Object.assign({}, cloneDeep(this.value));
        this.campsiteTypes = AppModel.campsiteTypes;
        this.personTypes = AppModel.persons;

        if(this.seasons) {
            this.buildModel(this.seasons);
        }
    },

    data() {
        return {
            prices: {},
            personCopy: {},
            isEditMode: !this.value.id,
            isLoading: false,
            apiErrors: {},
            onlyDayPrices: AppModel.onlyDayPrices,
        };
    },

    computed: {
        lang() {
            return AppModel.lang;
        },
    },

    watch: {
        seasons(seasons) {
            this.buildModel(seasons);
        },

        isEditMode(newValue, oldValue) {
            if(newValue === false) {
                this.apiErrors = {};
            }
        },
    },

    validator: null,

    methods: {
        savePerson() {
            this.$refs.observer.validate().then(result => {
                if(this.personCopy.id) {
                    this.updatePerson();
                } else {
                    this.addPerson();
                }
            }).catch(result => {
                console.log('From invalid!', result);
                Tracking.trackEvent('Person', 'updatePerson', 'formInvalid', null);
            });
        },

        addPerson() {
            this.onUpdateStart();
            this.personCopy.campground_slug = AppModel.activeCampground.slug;
            Api.persons.addPerson(this.personCopy)
                .then((response) => {
                    if(response.ok) {
                        this.personCopy.id = response.body.id;
                        this.onUpdateDone(response.data);
                    } else {
                        this.onUpdateFailed(response);
                    }
                }, () => {
                    this.onUpdateFailed();
                });
            Tracking.trackEvent('Person', 'savePerson', null, null);
        },

        updatePerson() {
            this.onUpdateStart();
            this.personCopy.campground_slug = AppModel.activeCampground.slug;
            Api.persons.updatePerson(this.personCopy)
                .then((response) => {
                    if(response.ok) {
                        this.onUpdateDone(response.data);
                        console.log(response.data);
                    } else {
                        this.onUpdateFailed(response);
                    }
                }, () => {
                    this.onUpdateFailed();
                });
            Tracking.trackEvent('Person', 'updatePerson', null, null);
        },

        deletePerson() {
            this.$emit('del');
        },

        toggleEditMode() {
            this.isEditMode = !this.isEditMode;
            if(!this.isEditMode) {
                if(!this.personCopy.id) {
                    this.deletePerson();
                } else {
                    this.personCopy = Object.assign({}, cloneDeep(this.value));
                    this.buildModel(this.seasons);
                }
            }
            Tracking.trackEvent('Person', 'toggleEditMode', null, null);
        },

        onUpdateStart() {
            this.isLoading = true;
        },

        onUpdateFailed(response = null) {
            if(response) {
                console.log('ERROR:', response.body);
                if(typeof response.body !== 'string') {
                    this.apiErrors = response.body;
                } else {
                    EventBus.$emit(EventBus.SHOW_NOTIFICATION, {
                        title: `<h3>${this.$t('errors.500.title')}</h3>`,
                        direction: 'Up',
                        type: 'danger',
                        body: this.$t('errors.500.content')
                    });
                }
            } else {
                EventBus.$emit(EventBus.SHOW_NOTIFICATION, {
                    title: `<h3>${this.$t('generic.formErrorHeadline')}</h3>`,
                    direction: 'Up',
                    type: 'danger',
                    body: this.$t('generic.formError')
                });
            }
            this.isLoading = false;
            Tracking.trackEvent('Person', 'updatePerson', 'failed', null);
        },

        onUpdateDone(data) {
            this.isLoading = false;
            this.isEditMode = false;
            this.$emit('input', data);
            EventBus.$emit(EventBus.UPDATE_CHECKS);
            EventBus.$emit(EventBus.SHOW_NOTIFICATION, {
                title: `<h3>${this.$t('generic.saveSuccessHeadline')}</h3>`,
                direction: 'Up',
                body: this.$t('generic.saveSuccess')
            });
            Tracking.trackEvent('Person', 'updatePerson', 'success', null);
        },

        getPersonPrice(type, id) {
            let price = find(this.value.prices, {'campsite_type': type, 'season_id': id});
            if(price) {
                return price.value;
            }
        },

        onPriceChanged(type, id, value) {
            let price = find(this.personCopy.prices, {'campsite_type': type, 'season_id': id});
            if(price) price.value = value;

            if(price) {
                price.value = value;
            } else {
                this.personCopy.prices.push({
                    'campsite_type': type,
                    'season_id': id,
                    'value': value
                });
            }
        },

        buildModel(seasons) {
            const campsiteTypes = this.campsiteTypes;
            for(let season in seasons) {
                if(!seasons[season].is_lock) {
                    this.$set(this.prices, seasons[season].id, {});
                    if(!this.prices[seasons[season].id]) {
                        this.$set(this.prices, seasons[season].id, {});
                    }
                    for(let key in campsiteTypes) {
                        if(campsiteTypes.hasOwnProperty(key)) {
                            this.$set(this.prices[seasons[season].id], key, this.getPersonPrice(key, seasons[season].id));
                        }
                    }
                }
            }
        },

        fromFieldClicked(fieldName) {
            Tracking.trackEvent('Person', 'formField', fieldName, null);
        }
    },
};
</script>

<style lang="scss">
    @import 'person.scss';
</style>

<style lang="css" scoped>
    .red {
        color: red;
    }
</style>



// WEBPACK FOOTER //
// src/views/components/person/person.vue